import { events, leads } from "./../common/db";
import { updateSponsorLead } from "./../common/api";
import Dexie from "dexie";
import * as Sentry from "@sentry/browser";

//sorting data based on bookingID
export const sorter = (a, b) => {
  const isNumber = (v) => (+v).toString() === v;
  const aPart = a.bookingId || "";
  const bPart = b.bookingId || "";

  // Handle empty bookingIds
  if (!aPart && !bPart) return 0;
  if (!aPart) return 1;
  if (!bPart) return -1;

  let i = 0;
  let len = Math.min(aPart.length, bPart.length);
  while (i < len && aPart[i] === bPart[i]) {
    i++;
  }
  if (i === len) {
    return aPart.length - bPart.length;
  }
  if (isNumber(aPart[i]) && isNumber(bPart[i])) {
    return aPart[i] - bPart[i];
  }
  return aPart[i].localeCompare(bPart[i]);
};

//conversion of similar kind of time 1702030676035 to 11th Dec 2023, 12:43 PM
// Uses the same format as changeDate for consistency
export const timeConvertor = (time) => {
  if (!time) return "";
  
  let convert = parseInt(time);
  const date = new Date(convert);
  
  // Get day with ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
  const day = date.getDate();
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };
  
  // Format month as short name (Jan, Feb, Mar, etc.)
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  
  // Get year
  const year = date.getFullYear();
  
  // Format time without seconds (12:43 PM)
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const hour12 = hours % 12 || 12;
  const ampm = hours < 12 ? "AM" : "PM";
  const timeString = `${hour12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  
  // Return formatted date: "11th Dec 2023, 12:43 PM"
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}, ${timeString}`;
};

//conversion of similar kind of time 2023-12-10T19:13:20.520Z to 13th Feb 2026, 2:30 PM

export const changeDate = (createdAt) => {
  if (!createdAt) return "";
  
  const date = new Date(createdAt);
  
  // Get day with ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
  const day = date.getDate();
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };
  
  // Format month as short name (Jan, Feb, Mar, etc.)
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  
  // Get year
  const year = date.getFullYear();
  
  // Format time without seconds (2:30 PM)
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const hour12 = hours % 12 || 12;
  const ampm = hours < 12 ? "AM" : "PM";
  const timeString = `${hour12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  
  // Return formatted date: "13th Feb 2026, 2:30 PM"
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}, ${timeString}`;
};

export const truncateText = (text, maxLength) =>{
  return text?.length > maxLength ? text?.slice(0, maxLength) + '...' : text;
}

//updating IndexedDB
export const updatingIndexedData = async (data) => {
  // data={
  //   key: Number,
  //   network: BooleanValue,
  //   comment: String,
  //   status: Number,
  //   name: String,
  //   emailId: String,
  //   phoneNumber: String,
  //   designation: String,
  //   organisation: String,
  //   createdAt: String,
  //   scannedBy: String
  // }
  await leads.update(data.key, {
    comment: data.comment,
    network: data.network.toString(),
    status: data.status? data.status: 2,
    ...(data.name && {name:data.name}),
    ...(data.emailId && {emailId:data.emailId}),
    ...(data.phoneNumber && {phoneNumber:data.phoneNumber}),
    ...(data.designation && {designation:data.designation}),
    ...(data.organisation && {organisation:data.organisation}),
    ...(data.createdAt && {createdAt:data.createdAt}),
    ...(data.scannedBy && {scannedBy:data.scannedBy})
  });
};

//update lead api
export const updateLeadApi = async (objectOfLeadDetials) => {
  try {
    const res = await updateSponsorLead(objectOfLeadDetials);

    // Check for GraphQL errors in response
    if (res.errors && res.errors.length > 0) {
      console.error("GraphQL errors in updateLeadApi:", res.errors);
      throw new Error(res.errors[0]?.message || "Failed to sync lead");
    }

    return res;
  } catch (err) {
    console.error("Error in updateLeadApi:", err);
    // Re-throw so callers can handle the error with their own UI
    throw err;
  }
};

export const navigateShareOption = (fetch_url, sponsorCode) =>{
  if(fetch_url.includes("localhost")){
    const port = new URL(fetch_url).port || '3001';
    return `http://localhost:${port}/login?sponsorEventCode=${sponsorCode}`;
  }
  else if(fetch_url.includes("pre-dev")){
    return `https://pre-dev-sponsor.konfhub.com/login?sponsorEventCode=${sponsorCode}`
  }
  else if(fetch_url.includes("dev-")){
    return `https://dev-sponsor.konfhub.com/login?sponsorEventCode=${sponsorCode}`
  }
  else{
    return `https://sponsor.konfhub.com/login?sponsorEventCode=${sponsorCode}`
  }
}

export const addAllEventsToIndexDB = (allEvents) =>{
  let eventsIndexedDBArr = [];

  allEvents.map((entry, index) => {
    eventsIndexedDBArr.push({
      uniqueId: entry["startDate"] + index,
      eventId: entry["eventId"],
      eventName: entry["eventName"],
      sponsorCode: entry["sponsorCode"],
      startDate: entry["startDate"],
      startTime: entry["startTime"],
      endDate: entry["endDate"],
      endTime: entry["endTime"],
      leadsCount: entry["leadsCount"],
      city: entry["city"],
      eventPoster: entry["eventPoster"] || null,
      createdAt: entry["createdAt"] || null
    });
  });
  events
    .bulkAdd(eventsIndexedDBArr)
    .then(() => {
      console.log("Done adding all Events");
    })
    .catch(Dexie.BulkError, (e) => {
      console.error("Some events did not succeed");
    });
}

 export const safeJSONParse = (data, fallback = null) => {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error("JSON.parse error:", error);
      return fallback;
    }
  };  
  export const safeJSONStringify = (data, fallback = "") => {
    try {
      return JSON.stringify(data);
    } catch (error) {
      console.error("JSON.stringify error:", error);
      return fallback;
    }
  };

  export const Dubai ="dubai"

  // --- Helper: sanitize sensitive data ---
  export const sanitizeData = data => {
    if (!data) return data;
    const clone = { ...data };
    delete clone?.password;
    delete clone?.token;
    delete clone?.jwtToken;
    return clone;
  };

//sentry function
//This is to capture message for client supported for staging and production both
  export const captureSentryMessageForClientProd = (message, details, level = "debug") => {
	Sentry.captureMessage(message, {
		level: level,
		extra: {
			...details
		}
	});
};

//This is to capture api errors/exception for client supported for staging and production both
export const captureSentryExceptionForClientProd = (error, context = {}) => {
	const { status, url, method, requestData, responsePayload } = context;

	Sentry.captureMessage(error.message, {
		tags: {
			service: "axios",
			api_url: String(url),
			http_method: String(method),
			status_code: String(status)
		},
		extra: {
			requestData: sanitizeData(requestData),
			responsePayload
		}
	});
}
// TODO: Update this date to the actual production release date when pushing to prod
// Teams feature is only available for events created after this date
export const TEAMS_FEATURE_RELEASE_DATE = "2026-03-01T00:00:00.000Z";

// Check if Teams feature should be shown for an event based on its createdAt date
export const shouldShowTeamsFeature = (eventCreatedAt) => {
  if (!eventCreatedAt) return false;
  const eventDate = new Date(eventCreatedAt);
  const releaseDate = new Date(TEAMS_FEATURE_RELEASE_DATE);
  return eventDate >= releaseDate;
};